/**
 * CSRF (Cross-Site Request Forgery) Protection Service
 * Implements double-submit cookie pattern for CSRF prevention
 */

import { auditLogger } from './auditLogger';

export interface CSRFConfig {
  apiUrl: string;
  tokenHeader: string;
  tokenCookie: string;
  enabled: boolean;
}

export class CSRFTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CSRFTokenError';
  }
}

class CSRFProtection {
  private token: string | null = null;
  private config: CSRFConfig;
  private refreshTimer: ReturnType<typeof setInterval> | null = null;

  constructor(config?: Partial<CSRFConfig>) {
    this.config = {
      apiUrl: import.meta.env.VITE_API_URL || 'https://api.withinehr.com',
      tokenHeader: 'X-CSRF-Token',
      tokenCookie: 'ww_csrf_token',
      enabled: true,
      ...config,
    };
  }

  async initialize(): Promise<void> {
    if (!this.config.enabled) return;

    try {
      await this.refreshToken();

      // Refresh token every 30 minutes
      this.refreshTimer = setInterval(
        () => {
          this.refreshToken().catch(console.error);
        },
        30 * 60 * 1000,
      );
    } catch (error) {
      console.error('[CSRF] Failed to initialize:', error);
      await auditLogger.logSecurityEvent('CSRF_INIT_FAILED', 'failure', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
    }
  }

  async getToken(): Promise<string> {
    if (!this.config.enabled) return '';

    if (this.token) return this.token;

    await this.refreshToken();

    if (!this.token) {
      throw new CSRFTokenError('Failed to obtain CSRF token');
    }

    return this.token;
  }

  /**
   * Refresh the CSRF token from the API
   */
  async refreshToken(): Promise<string | null> {
    try {
      const response = await fetch(`${this.config.apiUrl}/api/v1/csrf/token`, {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new CSRFTokenError(`Failed to fetch CSRF token: ${response.status}`);
      }

      const data = await response.json();

      if (!data.csrf_token) {
        throw new CSRFTokenError('CSRF token not found in response');
      }

      const token: string = String(data.csrf_token);
      this.token = token;

      // Also store in sessionStorage as backup
      if (this.token) {
        sessionStorage.setItem('ww_csrf_token', this.token);
      }

      await auditLogger.logSecurityEvent('CSRF_TOKEN_REFRESHED', 'success');

      return token;
    } catch (error) {
      console.error('[CSRF] Failed to refresh token:', error);
      await auditLogger.logSecurityEvent('CSRF_TOKEN_REFRESH_FAILED', 'failure', {
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      throw error;
    }
  }

  /**
   * FIXED: validateToken now accepts string | null
   * and handles substring safely (fix for line 110 error)
   */
  async validateToken(token: string | null): Promise<boolean> {
    if (!this.config.enabled) return true;

    if (!token) {
      await auditLogger.logSecurityEvent('CSRF_TOKEN_MISSING', 'failure');
      return false;
    }

    if (!this.token) {
      await this.getToken();
    }

    const expected = this.token ?? '';
    const isValid = token === expected;

    if (!isValid) {
      await auditLogger.logSecurityEvent('CSRF_TOKEN_MISMATCH', 'failure', {
        expected: expected.substring(0, 10) + '...',
        received: token.substring(0, 10) + '...',
      });
    }

    return isValid;
  }

  async addTokenToHeaders(headers: HeadersInit = {}): Promise<HeadersInit> {
    if (!this.config.enabled) return headers;

    const token = await this.getToken();

    return {
      ...headers,
      [this.config.tokenHeader]: token,
    };
  }

  async addTokenToFormData(formData: FormData): Promise<FormData> {
    if (!this.config.enabled) return formData;

    const token = await this.getToken();
    formData.append('csrf_token', token);

    return formData;
  }

  clearToken(): void {
    this.token = null;
    sessionStorage.removeItem('ww_csrf_token');
  }

  destroy(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
    this.clearToken();
  }

  updateConfig(config: Partial<CSRFConfig>): void {
    this.config = { ...this.config, ...config };
  }
}

let csrfProtectionInstance: CSRFProtection | null = null;

export function getCSRFProtection(config?: Partial<CSRFConfig>): CSRFProtection {
  if (!csrfProtectionInstance) {
    csrfProtectionInstance = new CSRFProtection(config);
  }
  return csrfProtectionInstance;
}

export const csrfProtection = getCSRFProtection();
